import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { FileUploadComponent } from '@appkit4/angular-components/fileupload';
import { EnvironmentsService } from '@core/services';
import { ImportExtensions } from '@shared/constants/import-extensions';
import { UploadFile } from '@shared/models';
import { UtilityService } from '@shared/services/utility.service';
import { Observable } from 'rxjs';
import { FileParameter, FileResponse } from 'src/app/web-api-client';

@Component({
  standalone: false,
  selector: 'gstar-import-file-modal',
  templateUrl: './import-file-modal.component.html',
  styleUrls: ['./import-file-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportFileModalComponent {
  environment = inject(EnvironmentsService);
  private utilityService = inject(UtilityService);

  @Input({ required: true }) title!: string;
  @Input() description!: string;
  @Input({ required: true }) templateFileName!: string;
  @Input() modalId!: string;
  @Input() importFn?: (fileParameter: FileParameter) => void;
  @Input() downloadTemplateFn?: () => Observable<FileResponse>;

  uploader: Signal<FileUploadComponent> =
    viewChild.required<FileUploadComponent>('uploader');

  hasFileSelected: WritableSignal<boolean> = signal(false);
  file!: FileParameter;
  destroyRef = inject(DestroyRef);
  allowedExtensions: string[] = ImportExtensions;

  fileuploadConfig = {
    multiple: false,
    type: 'inline',
  };

  customUploader = {
    maxFileSize: 10 * 1024 * 1024,
    autoUpload: false,
  };

  addFile(data: UploadFile): void {
    if (this.uploader().uploader.queue.length > 1) {
      for (let i: number = 1; i < this.uploader().uploader.queue.length; i++) {
        this.uploader()?.uploader.queue.pop();
      }
    } else if (this.isAllowed(data.file.name)) {
      const fileParameter: FileParameter = {
        fileName: data.file.name,
        data: data.file.rawFile,
      };
      this.hasFileSelected.set(true);
      this.file = fileParameter;
    }
  }

  deleteFile(): void {
    this.hasFileSelected.set(false);
  }

  importFile(): void {
    this.importFn?.(this.file);
  }

  downloadTemplate(): void {
    this.downloadTemplateFn?.().subscribe((template: FileResponse) => {
      if (template.fileName === undefined) {
        template.fileName = this.templateFileName;
      }
      this.utilityService.download(template.fileName!, template.data);
    });
  }

  isAllowed(fileName: string): boolean {
    return (
      this.allowedExtensions.filter((ext: string) =>
        fileName.endsWith(ext.toLowerCase())
      ).length > 0
    );
  }
}
